import React, { useEffect, useMemo, useRef, useState } from "react";
import { RefreshCw } from "lucide-react";
import { listBullseyeMatchLeaderboards } from "../../lib/leaderboardApi";
import { buildBullseyeBulletinDocument, buildBullseyePostedSections } from "../Leaderboard/bullseyeBulletinModel";
import { BullseyeBulletinPrintControls } from "../Leaderboard/BullseyeBulletinPrint";
import { completedBullseyeSections } from "./completedResults";
import LiveStandingsExport from "./LiveStandingsExport";
import RelayPdfExport from "./RelayPdfExport";
import MatchProgram from "./MatchProgram";

export default function DirectorReportDownload({ match, document }) {
  const needsScores = ["standings", "bulletin"].includes(document);
  const [snapshot, setSnapshot] = useState({ loading: true, rows: [] });
  const [revision, setRevision] = useState(0);
  const [pdfSection, setPdfSection] = useState("all");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const busy = useRef(false);

  useEffect(() => {
    if (!needsScores) return undefined;
    let active = true;
    setSnapshot({ loading: true, rows: [] });
    // A report must contain every page. Failed pagination must never export a partial worksheet.
    listBullseyeMatchLeaderboards({ matchId: match.matchId, requireComplete: true })
      .then(rows => { if (active) setSnapshot({ loading: false, rows }); })
      .catch(error => { if (active) setSnapshot({ loading: false, rows: [], error: error.message || "Scores could not load." }); });
    return () => { active = false; };
  }, [match.matchId, needsScores, revision]);

  const sections = useMemo(() => buildBullseyePostedSections(snapshot.rows, match.bullseyeResultSettings),
    [snapshot.rows, match.bullseyeResultSettings]);
  const standings = useMemo(() => completedBullseyeSections(snapshot.rows, sections), [snapshot.rows, sections]);
  const selectedSection = sections.some(section => section.key === pdfSection) ? pdfSection : "all";
  async function downloadBulletin() {
    if (busy.current || snapshot.loading || snapshot.error || !sections.length) return;
    busy.current = true; setPdfBusy(true); setPdfError("");
    try {
      const { downloadBullseyeBulletinPdf } = await import("../../lib/bullseyeBulletinPdf");
      await downloadBullseyeBulletinPdf(buildBullseyeBulletinDocument(match, sections, selectedSection));
    } catch (error) { setPdfError(error.message || "The bulletin could not be downloaded."); }
    finally { busy.current = false; setPdfBusy(false); }
  }

  if (document === "relays") return <RelayPdfExport match={match} />;
  if (document === "program") return <MatchProgram matchId={match.matchId} family={match.family} showEmpty />;
  if (!needsScores) return <p>This report is not available for this match.</p>;
  if (snapshot.loading) return <p role="status">Loading complete match scores...</p>;
  if (snapshot.error) return <div role="alert"><p>{snapshot.error}</p><button type="button" className="dp-button" onClick={() => setRevision(value => value + 1)}>Retry scores</button></div>;
  if (!sections.length) return <p role="status">No recorded scores are available to export.</p>;
  return <>
    <div className="director-report-tools"><span>{snapshot.rows.length} competitors loaded</span>
      <button type="button" className="dp-button" disabled={pdfBusy} onClick={() => setRevision(value => value + 1)}><RefreshCw size={15} aria-hidden="true" />Refresh scores</button>
    </div>
    {document === "standings" && <LiveStandingsExport match={match} sections={standings} currentKey="all" />}
    {document === "bulletin" && <BullseyeBulletinPrintControls sections={sections} selectedSection={selectedSection}
      onSectionChange={setPdfSection} onPrint={downloadBulletin} busy={pdfBusy} error={pdfError} />}
  </>;
}
